import React, { useState, useEffect } from "react";
import "../styles/StudentEnquiryForm.css";
import {
  addStudentEnquiry,
  getStudentEnquiries,
  updateFollowUp,
} from "../api/StudentDetails";
import { StudentEnquiry } from "../types";

const StudentEnquiryForm: React.FC = () => {
  const [studentEnquiryForm, setStudentEnquiryForm] = useState<StudentEnquiry>({
    firstName: "",
    lastName: "",
    address: "",
    mobileNumber: "",
    createdBy: "Admin",
  });

  const resetForm = () => {
    setStudentEnquiryForm({
      firstName: "",
      lastName: "",
      address: "",
      mobileNumber: "",
      createdBy: "Admin",
    });
    //setSubmitted(false);
    //setErrors({});
  };

  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [enquiries, setEnquiries] = useState<StudentEnquiry[]>([]);
  const [loading, setLoading] = useState(false);

  // Auto-hide success message after 5 seconds
  useEffect(() => {
    if (submitted) {
      const timer = setTimeout(() => {
        setSubmitted(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [submitted]);

  // Fetch enquiries on component mount
  useEffect(() => {
    fetchEnquiries();
  }, []);

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const response = await getStudentEnquiries(false);
      debugger;
      if (response.data) {
        setEnquiries(response.data);
      }
    } catch (error) {
      console.error("Error fetching enquiries:", error);
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const nextErrors: { [k: string]: string } = {};

    if (!studentEnquiryForm.firstName.trim()) {
      nextErrors.firstName = "First name is required";
    }

    if (!studentEnquiryForm.lastName.trim()) {
      nextErrors.lastName = "Last name is required";
    }

    if (!studentEnquiryForm.address.trim()) {
      nextErrors.address = "Address is required";
    }

    if (!studentEnquiryForm.mobileNumber.trim()) {
      nextErrors.mobileNumber = "Mobile number is required";
    } else if (!/^\+?\d{7,15}$/.test(studentEnquiryForm.mobileNumber.trim())) {
      nextErrors.mobileNumber = "Enter a valid mobile number";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      const res = await addStudentEnquiry(studentEnquiryForm);
      console.log("Enquiry submitted successfully:", res);
      resetForm();
      setSubmitted(true);
      setErrors({});
      // Refresh the enquiries list after successful submission
      await fetchEnquiries();
    } catch (error) {
      console.error("Error submitting enquiry:", error);
      setErrors({ general: "Failed to submit enquiry. Please try again." });
    }
  };

  const handleFollowUp = async (mobileNumber: string) => {
    try {
      await updateFollowUp(mobileNumber);
      await fetchEnquiries(); // Refresh the list
    } catch (error) {
      console.error("Error updating follow-up:", error);
    }
  };

  const handleDelete = async (enquiryId: number | undefined) => {
    if (!enquiryId) return;

    const confirmed = window.confirm(
      "Are you sure you want to delete this enquiry?"
    );
    if (!confirmed) return;

    try {
      // Optimistically remove from UI
      setEnquiries((prevEnquiries) =>
        prevEnquiries.filter((e) => e.id !== enquiryId)
      );

      // If you have a delete API endpoint, call it here
      // await deleteStudentEnquiry(enquiryId);
    } catch (error) {
      console.error("Error deleting enquiry:", error);
      // Revert on error
      await fetchEnquiries();
    }
  };

  const formatDate = (date?: string | Date) => {
    if (!date) return "N/A";
    const d = typeof date === "string" ? new Date(date) : date;
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="enquiry-page">
      <div className="enquiry-card">
        <div className="enquiry-header">
          <p>Please provide your details and we’ll get back to you.</p>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          {errors.general && (
            <div className="error-banner">
              <span className="error-icon">⚠️</span>
              {errors.general}
            </div>
          )}
          <div className="form-field">
            <label htmlFor="firstName">First Name</label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              className={errors.firstName ? "has-error" : ""}
              value={studentEnquiryForm.firstName}
              onChange={(e) =>
                setStudentEnquiryForm({
                  ...studentEnquiryForm,
                  firstName: e.target.value,
                })
              }
              placeholder="John"
            />
            {errors.firstName && (
              <div className="error-text">{errors.firstName}</div>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="lastName">Last Name</label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              className={errors.lastName ? "has-error" : ""}
              value={studentEnquiryForm.lastName}
              onChange={(e) =>
                setStudentEnquiryForm({
                  ...studentEnquiryForm,
                  lastName: e.target.value,
                })
              }
              placeholder="Doe"
            />
            {errors.lastName && (
              <div className="error-text">{errors.lastName}</div>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="address">Address</label>
            <textarea
              id="address"
              name="address"
              className={errors.address ? "has-error" : ""}
              value={studentEnquiryForm.address}
              onChange={(e) =>
                setStudentEnquiryForm({
                  ...studentEnquiryForm,
                  address: e.target.value,
                })
              }
              placeholder="123 Main Street, City, State"
              rows={3}
            />
            {errors.address && (
              <div className="error-text">{errors.address}</div>
            )}
          </div>

          <div className="form-field">
            <label htmlFor="mobileNumber">Mobile Number</label>
            <input
              id="mobileNumber"
              name="mobileNumber"
              type="tel"
              className={errors.mobileNumber ? "has-error" : ""}
              value={studentEnquiryForm.mobileNumber}
              onChange={(e) =>
                setStudentEnquiryForm({
                  ...studentEnquiryForm,
                  mobileNumber: e.target.value,
                })
              }
              placeholder="+1234567890"
            />
            {errors.mobileNumber && (
              <div className="error-text">{errors.mobileNumber}</div>
            )}
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary">
              Submit Enquiry
            </button>
          </div>
          {submitted && (
            <div className="success-toast">
              <div className="success-icon">✅</div>
              <div className="success-content">
                <h4>Enquiry Submitted Successfully!</h4>
                <p>Thank you for your interest. We will contact you soon.</p>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Enquiries Table */}
      <div className="enquiries-table-card">
        <div className="table-header">
          <h2>Student Enquiries</h2>
          <button
            onClick={fetchEnquiries}
            className="refresh-btn"
            disabled={loading}
          >
            {loading ? "Loading..." : "🔄 Refresh"}
          </button>
        </div>

        {loading && enquiries.length === 0 ? (
          <div className="loading-state">Loading enquiries...</div>
        ) : enquiries.length === 0 ? (
          <div className="empty-state">No enquiries found.</div>
        ) : (
          <div className="table-container">
            <table className="enquiries-table">
              <thead>
                <tr>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Mobile Number</th>
                  <th>Address</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {enquiries.map((enquiry) => (
                  <tr key={enquiry.id}>
                    <td>{enquiry.firstName}</td>
                    <td>{enquiry.lastName}</td>
                    <td>{enquiry.mobileNumber}</td>
                    <td className="address-cell">{enquiry.address}</td>
                    <td>
                      <span
                        className={`status-badge ${
                          enquiry.followUp ? "resolved" : "pending"
                        }`}
                      >
                        {enquiry.followUp ? "done" : "pending"}
                      </span>
                    </td>
                    <td>{formatDate(enquiry.createdDate)}</td>
                    <td className="actions-cell">
                      <div className="action-buttons">
                        {!enquiry.followUp && (
                          <button
                            className="resolved-btn"
                            onClick={() => handleFollowUp(enquiry.mobileNumber)}
                            title="Mark as Resolved"
                          >
                            ✓ Resolved
                          </button>
                        )}
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(enquiry.id)}
                          title="Delete Enquiry"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentEnquiryForm;
